import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { LAYER_ATTR, useLayer } from "../layer";

/**
 * A button that opens a flush, bordered menu under it — or above it, when
 * there's more room there. The menu lives in <body>, so a Sheet or an
 * overflow-hidden card can't clip it. Closes on outside click, Escape and scroll.
 */
export function Popover({
  trigger,
  children,
  align = "left",
  className,
  menuClassName,
  label,
}: {
  trigger: (p: { open: boolean; toggle: () => void }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
  className?: string;
  /** Sizing for the floating menu itself (width, max-height). */
  menuClassName?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useLayer(open, ref, layerRef, close, { align });
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div ref={ref} className={cn("relative", className)}>
      {trigger({ open, toggle: () => setOpen((o) => !o) })}
      {open &&
        createPortal(
          <div
            ref={layerRef}
            role="menu"
            aria-label={label}
            {...{ [LAYER_ATTR]: "" }}
            className={cn(
              "animate-pop-k invisible fixed left-0 top-0 z-[85] min-w-[240px] overflow-y-auto rounded-lg border border-line-strong bg-surface p-1.5 shadow-float",
              menuClassName,
            )}
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function MenuItem({
  onClick,
  children,
  active,
  tone,
}: {
  onClick: () => void;
  children: ReactNode;
  active?: boolean;
  tone?: "rhodo";
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={cn(
        "flex min-h-10 w-full items-center gap-3 rounded-sm px-3 text-left text-[14px]",
        active && "bg-deodar-tint font-semibold",
        tone === "rhodo"
          ? "text-rhodo hover:bg-[color-mix(in_srgb,var(--rhododendron)_9%,var(--surface))]"
          : !active && "hover:bg-sunk",
      )}
    >
      {children}
    </button>
  );
}
